import { useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import api from "../../lib/api"
import { toastError } from "../../lib/toast"
import ModuleLayout from "../../components/admin/ModuleLayout"
import DataTable, { type DataTableColumn, type DataTableSort } from "../../components/ui/table/DataTable"
import { RowActionsMenu } from "../../components/admin/RowActionsMenu"
import { btnSecondary } from "../../components/admin/FormComponents"
import { hasCurrentPolicy } from "../../lib/auth"

type Attempt = {
  id: string
  is_team_attempt: boolean
  team_name: string | null
  student_names: string[]
  submitted_by: string | null
  student_email: string | null
  score: number
  total_questions: number
  passed: boolean
  source: "live" | "backup"
  overridden: boolean
  overridden_by: string | null
  overridden_at: string | null
  created_at: string
}

const PAGE_SIZE = 20

export default function SectionQuizAttemptsPage() {
  const { sectionQuizId } = useParams<{ sectionQuizId: string }>()
  const navigate = useNavigate()
  const [attempts, setAttempts] = useState<Attempt[]>([])
  const [loading, setLoading] = useState(true)
  const [sort, setSort] = useState<DataTableSort>({ column: "created_at", dir: "desc" })
  const [page, setPage] = useState(1)

  const load = useCallback(async () => {
    if (!sectionQuizId) return
    setLoading(true)
    try {
      const res = await api.get(`/academy/section-quizzes/${sectionQuizId}/attempts/admin`)
      setAttempts(res.data.data?.attempts ?? [])
    } catch (err) {
      toastError(err)
    } finally {
      setLoading(false)
    }
  }, [sectionQuizId])

  useEffect(() => { void load() }, [load])

  const passRate = attempts.length > 0
    ? Math.round((attempts.filter((a) => a.passed).length / attempts.length) * 100)
    : 0
  const avgScore = attempts.length > 0
    ? Math.round(attempts.reduce((sum, a) => sum + a.score, 0) / attempts.length)
    : 0
  const teamAttempts = attempts.filter((a) => a.is_team_attempt).length

  const sorted = [...attempts].sort((a, b) => {
    if (!sort) return 0
    const dir = sort.dir === "asc" ? 1 : -1
    switch (sort.column) {
      case "student_names":
        return a.student_names.join(", ").localeCompare(b.student_names.join(", ")) * dir
      case "score":
        return (a.score - b.score) * dir
      case "created_at":
        return (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()) * dir
      default:
        return 0
    }
  })
  const pageRows = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const columns: DataTableColumn<Attempt>[] = [
    {
      key: "student_names",
      label: "Estudiante(s)",
      sortable: true,
      render: (a) => (
        <div>
          {a.is_team_attempt && (
            <p className="mb-0.5 inline-flex rounded-full bg-blue-100 px-1.5 py-0.5 text-[11px] font-semibold text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">
              Equipo{a.team_name ? `: ${a.team_name}` : ""}
            </p>
          )}
          <p className="font-medium text-gray-900 dark:text-white">{a.student_names.join(", ")}</p>
          {a.student_email && <p className="text-xs text-gray-500 dark:text-gray-400">{a.student_email}</p>}
        </div>
      ),
    },
    { key: "score", label: "Puntaje", sortable: true, render: (a) => String(a.score) },
    {
      key: "passed",
      label: "Aprobó",
      render: (a) => (
        <div className="flex items-center gap-1.5">
          {a.passed ? (
            <span className="font-medium text-green-600">✓</span>
          ) : (
            <span className="font-medium text-error-600 dark:text-error-400">✗</span>
          )}
          {a.overridden && (
            <span title={a.overridden_by ? `Anulado por ${a.overridden_by}` : "Resultado anulado"}
              className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
              Anulado
            </span>
          )}
        </div>
      ),
    },
    {
      key: "created_at",
      label: "Fecha",
      sortable: true,
      render: (a) => <span className="text-gray-500">{new Date(a.created_at).toLocaleString("es-CO")}</span>,
    },
    {
      key: "actions",
      label: "",
      headerAriaLabel: "Acciones",
      align: "right",
      render: (a) => (
        <RowActionsMenu
          label="Acciones del intento"
          actions={[
            {
              label: "Ver detalle",
              icon: <span>👁</span>,
              to: `/instructor/section-quizzes/attempts/${a.id}`,
            },
          ]}
        />
      ),
    },
  ]

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Contenido", { label: "Exámenes IA", to: "/instructor/section-quizzes" }, "Intentos"]}
      title="Intentos de examen"
      subtitle="Qué respondió cada estudiante o equipo, y cuál era la respuesta correcta"
      actions={
        <>
          {hasCurrentPolicy("academy:quizzes-teach:edit") && (
            <button type="button" className={btnSecondary} onClick={() => navigate(`/instructor/section-quizzes/${sectionQuizId}/regrade`)}>
              Reevaluar intentos
            </button>
          )}
          <button type="button" className={btnSecondary} onClick={() => navigate(-1)}>Volver</button>
        </>
      }
      tabs={[]}
      metrics={[
        { label: "Intentos totales", value: String(attempts.length) },
        { label: "Intentos de equipo", value: String(teamAttempts) },
        {
          label: "Tasa de aprobación",
          value: `${passRate}%`,
          tone: passRate >= 70 ? "good" : passRate >= 40 ? "warning" : "danger",
        },
        { label: "Puntuación promedio", value: String(avgScore) },
      ]}
    >
      <DataTable
        columns={columns}
        rows={pageRows}
        rowKey={(a) => a.id}
        loading={loading}
        emptyMessage="Sin intentos aún."
        sort={sort}
        onSortChange={(next) => { setSort(next); setPage(1) }}
        page={page}
        pageSize={PAGE_SIZE}
        total={sorted.length}
        onPageChange={setPage}
      />
    </ModuleLayout>
  )
}
